import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { Textarea } from '@orisini/ui/components/textarea'
import { useConfirm } from '@orisini/ui/components/confirm'
import { ago, discoverApi, type Topic, type TopicInput } from '../../discover/lib/discover'
import type { Workspace } from '../lib/accounts'

const KOSONG: TopicInput = { name: '', keywords: '', excludeKeywords: '', instructions: '', active: true }

/**
 * Topik yang dipantau akun ini. Orisini memeriksa berita baru setiap 15 menit
 * dan memasukkan yang relevan ke Discover — iramanya bukan pengaturan user.
 */
export function TopicsTab({ workspace: w }: { workspace: Workspace }) {
  const confirm = useConfirm()
  const [topics, setTopics] = useState<Topic[] | null>(null)
  /** id topik yang sedang diedit, 'new' untuk topik baru, null = tidak ada form. */
  const [editing, setEditing] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      setTopics(await discoverApi.topics(w.id))
    } catch (err) {
      setTopics([])
      toast.error(err instanceof Error ? err.message : "Couldn't load topics")
    }
  }, [w.id])

  useEffect(() => void muat(), [muat])

  const periksa = async (t: Topic) => {
    setBusy(`run:${t.id}`)
    try {
      const { outcome } = await discoverApi.runTopic(t.id)
      if (outcome.status === 'failed') toast.error(`Couldn't check ${t.name}: ${outcome.error}`)
      else if (outcome.status === 'skipped') toast.info(`${t.name} is already being checked`)
      else toast.success(outcome.added ? `${outcome.added} new ${outcome.added === 1 ? 'story' : 'stories'} in Discover` : 'No new stories this time')
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't check this topic")
    } finally {
      setBusy(null)
    }
  }

  const hapus = async (t: Topic) => {
    const ok = await confirm({
      title: `Remove the topic "${t.name}"?`,
      description: 'Orisini stops looking for it. Stories it already found stay in Discover.',
      confirmLabel: 'Remove',
      destructive: true,
    })
    if (!ok) return
    setBusy(`del:${t.id}`)
    try {
      await discoverApi.deleteTopic(t.id)
      toast.success('Topic removed')
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't remove the topic")
    } finally {
      setBusy(null)
    }
  }

  if (topics === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Orisini checks the news every 15 minutes and puts stories that fit these topics in{' '}
        <Link to={`/discover?account=${w.id}`} className="text-foreground underline-offset-4 hover:underline">Discover</Link>.
        A new topic looks back over the last 24 hours.
      </p>

      {topics.map((t) =>
        editing === t.id ? (
          <TopicForm
            key={t.id}
            initial={t}
            onCancel={() => setEditing(null)}
            onSave={async (input) => {
              await discoverApi.updateTopic(t.id, input)
              toast.success('Topic saved')
              setEditing(null)
              await muat()
            }}
          />
        ) : (
          <Card key={t.id} className="space-y-2 p-4">
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {t.name}
                  {!t.active && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">Paused</span>}
                </p>
                <p className="truncate text-sm text-muted-foreground">{t.keywords || 'No keywords — matched by topic name'}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="sm" disabled={!!busy || !t.active} onClick={() => periksa(t)} title="Check now" aria-label="Check now">
                  {busy === `run:${t.id}` ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                </Button>
                <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => setEditing(t.id)} title="Edit" aria-label="Edit">
                  <Pencil />
                </Button>
                <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => hapus(t)} title="Remove" aria-label="Remove">
                  <Trash2 />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {[
                t.newItems ? `${t.newItems} new in Discover` : null,
                t.lastRun
                  ? t.lastRun.status === 'failed'
                    ? `Last check failed ${ago(t.lastRun.at)}: ${t.lastRun.error}`
                    : t.lastRun.status === 'running'
                      ? 'Checking now…'
                      : `Checked ${ago(t.lastRun.at)} · ${t.lastRun.scanned} stories scanned${t.lastRun.usedAi ? '' : ' · keyword match only'}`
                  : 'Not checked yet',
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </Card>
        ),
      )}

      {editing === 'new' ? (
        <TopicForm
          initial={KOSONG}
          onCancel={() => setEditing(null)}
          onSave={async (input) => {
            await discoverApi.createTopic(w.id, input)
            toast.success('Topic added. Checking the last 24 hours…')
            setEditing(null)
            await muat()
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
        >
          <Plus className="size-4" /> Add topic
        </button>
      )}
    </div>
  )
}

function TopicForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: TopicInput
  onSave: (input: TopicInput) => Promise<void>
  onCancel: () => void
}) {
  const [form, setForm] = useState<TopicInput>({
    name: initial.name,
    keywords: initial.keywords,
    excludeKeywords: initial.excludeKeywords,
    instructions: initial.instructions,
    active: initial.active,
  })
  const [saving, setSaving] = useState(false)

  const kirim = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await onSave(form)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save the topic")
      setSaving(false)
    }
  }

  return (
    <Card className="p-4">
      <form onSubmit={kirim} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="topic-name">Topic name</Label>
          <Input id="topic-name" value={form.name} maxLength={60} autoFocus placeholder="Artificial Intelligence" onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="topic-kw">Keywords</Label>
          <Input id="topic-kw" value={form.keywords} maxLength={500} placeholder="OpenAI, Anthropic, Gemini, LLM" onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
          <p className="text-xs text-muted-foreground">Separate with commas. A story needs at least one of them to be considered.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="topic-ex">Leave out</Label>
          <Input id="topic-ex" value={form.excludeKeywords} maxLength={500} placeholder="rumour, gossip" onChange={(e) => setForm({ ...form, excludeKeywords: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="topic-ins">What you're looking for (optional)</Label>
          <Textarea
            id="topic-ins"
            value={form.instructions}
            maxLength={1000}
            rows={3}
            placeholder="Product launches and big research results. Skip funding news."
            onChange={(e) => setForm({ ...form, instructions: e.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Active
        </label>
        <div className="flex gap-2">
          <Button type="submit" disabled={saving || !form.name.trim()}>
            {saving && <Loader2 className="animate-spin" />}
            Save topic
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  )
}
